import { signal, computed, type Signal } from '@weave-framework/runtime';
import Card from '@weave-framework/ui/card';
import Badge from '@weave-framework/ui/badge';
import Table from '@weave-framework/ui/table';
import Icon from '@weave-framework/ui/icon';
import SlideToggle from '@weave-framework/ui/slide-toggle';
import Select from '@weave-framework/ui/select';
import Expansion from '@weave-framework/ui/expansion';
import Input from '@weave-framework/ui/input';
import Checkbox from '@weave-framework/ui/checkbox';
import ButtonToggle from '@weave-framework/ui/button-toggle';
import Button from '@weave-framework/ui/button';
import { openDialog } from '@weave-framework/ui/dialog';
import { snackbar } from '@weave-framework/ui/snackbar';
import { createFeed, type Txn, type TxnStatus, type FeedEvent } from '../lib/feed';

// Capitalized tags in dashboard.html resolve to these imports.
void Card;
void Badge;
void Table;
void Icon;
void SlideToggle;
void Select;
void Expansion;
void Input;
void Checkbox;
void ButtonToggle;
void Button;

const money = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;
const moneyCompact = (n: number): string =>
  n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${Math.round(n)}`;
const num = (n: number): string => Math.round(n).toLocaleString('en-US');

const rel = (secs: number): string => {
  if (secs <= 0) return 'now';
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
};

const NAV = [
  { icon: 'house', label: 'Overview' },
  { icon: 'shopping-cart', label: 'Payments' },
  { icon: 'user', label: 'Customers' },
  { icon: 'bell', label: 'Alerts' },
  { icon: 'settings', label: 'Settings' },
];

const NAV_SUB: Record<string, string> = {
  Overview: 'Real-time — every value below is a live signal',
  Payments: 'Transactions, refunds, and payout status',
  Customers: 'Sign-ups, upgrades, and churn',
  Alerts: 'Latency, error-rate, and region health',
  Settings: 'Workspace, billing, and API keys',
};

const ACCENTS = [
  { key: 'violet', color: '#7c6cff' },
  { key: 'emerald', color: '#2dd4a7' },
  { key: 'amber', color: '#f5a623' },
  { key: 'rose', color: '#ff5c8a' },
  { key: 'cyan', color: '#38bdf8' },
];

const FILTER_OPTS = [
  { value: 'all', label: 'All statuses' },
  { value: 'settled', label: 'Settled' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
];

const REGION_OPTS = [
  { value: 'eu', label: 'EU-West (Amsterdam)' },
  { value: 'us', label: 'US-East (Virginia)' },
  { value: 'ap', label: 'AP-South (Mumbai)' },
];

const DENSITY_OPTS = [
  { value: 'comfortable', label: 'Comfortable' },
  { value: 'compact', label: 'Compact' },
];

const STATUS_PANELS = [
  { id: 'eu', header: 'EU-West · operational', body: 'p95 68 ms · 0 incidents in the last 24h. Primary region for European traffic.' },
  { id: 'us', header: 'US-East · operational', body: 'p95 74 ms · autoscaled to 12 nodes during the last spike, now settled.' },
  { id: 'ap', header: 'AP-South · degraded', body: 'Elevated latency (p95 141 ms). Investigating a slow database replica.' },
];

const EVENT_ICON: Record<FeedEvent['kind'], string> = {
  signup: 'user',
  upgrade: 'arrow-up',
  payout: 'shopping-cart',
  alert: 'triangle-alert',
};

function statusPill(status: TxnStatus): HTMLElement {
  const el = document.createElement('span');
  el.className = `pill pill--${status}`;
  el.textContent = status[0].toUpperCase() + status.slice(1);
  return el;
}

/** The Actions cell — a real button that opens a dialog for that row. */
function actionButton(t: Txn): HTMLElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'weave-button weave-button--outline';
  b.textContent = 'View';
  b.onclick = () => openTxnDialog(t);
  return b;
}

/** Click a row's View → modal with the details and real actions → snackbar feedback. */
function openTxnDialog(t: Txn): void {
  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex; gap:8px; justify-content:flex-end;';
  const refund = document.createElement('button');
  refund.type = 'button';
  refund.className = 'weave-button weave-button--outline';
  refund.textContent = 'Refund';
  const approve = document.createElement('button');
  approve.type = 'button';
  approve.className = 'weave-button';
  approve.textContent = 'Approve';
  actions.append(refund, approve);

  const ref = openDialog({
    title: `Transaction #${t.id}`,
    content: `${t.merchant} — ${money(t.amount)} paid via ${t.method}. Current status: ${t.status}.`,
    actions,
  });
  refund.onclick = () => {
    ref.close('refunded');
    snackbar(`Refund issued to ${t.merchant}`, { action: 'Undo', duration: 3500 });
  };
  approve.onclick = () => {
    ref.close('approved');
    snackbar('Payment approved ✓', { duration: 2600 });
  };
}

interface Bar {
  i: number;
  /** bar height, 8–100, in the chart's 0–100 viewBox units */
  h: number;
  /** left edge in viewBox units (geometry precomputed so the template stays plain) */
  x: number;
  /** top edge (100 − h) */
  y: number;
  /** bar width in viewBox units */
  w: number;
  /** the trailing bar gets the highlight gradient */
  last: boolean;
}
function buildBars(xs: number[]): Bar[] {
  const min = Math.min(...xs);
  const max = Math.max(...xs);
  const range = max - min || 1;
  const n = xs.length;
  const slot = 100 / n;
  return xs.map((v, i) => {
    const h = Math.round((8 + ((v - min) / range) * 92) * 10) / 10;
    return { i, h, x: i * slot + slot * 0.12, y: 100 - h, w: slot * 0.76, last: i === n - 1 };
  });
}

interface Setup {
  nav: typeof NAV;
  accents: typeof ACCENTS;
  filterOpts: typeof FILTER_OPTS;
  statusPanels: typeof STATUS_PANELS;
  live: Signal<boolean>;
  now: Signal<number>;
  revenue: Signal<number>;
  users: Signal<number>;
  orders: Signal<number>;
  latency: Signal<number>;
  revenueDelta: Signal<number>;
  usersDelta: Signal<number>;
  events: Signal<FeedEvent[]>;
  bars: () => Bar[];
  visibleTxns: () => Txn[];
  columns: unknown[];
  trackTxn: (t: Txn) => number;
  filter: () => string;
  accent: () => string;
  activeNav: () => string;
  openPanels: () => string[];
  navSub: () => string;
  regionOpts: typeof REGION_OPTS;
  densityOpts: typeof DENSITY_OPTS;
  workspace: () => string;
  region: () => string;
  emailAlerts: () => boolean;
  density: () => string;
  setLive: (v: boolean) => void;
  setFilter: (v: unknown) => void;
  setAccent: (key: string) => void;
  selectNav: (label: string) => void;
  setOpenPanels: (v: string[]) => void;
  setWorkspace: (v: string) => void;
  setRegion: (v: unknown) => void;
  setEmailAlerts: (v: boolean) => void;
  setDensity: (v: unknown) => void;
  saveSettings: () => void;
  money: (n: number) => string;
  moneyCompact: (n: number) => string;
  num: (n: number) => string;
  rel: (s: number) => string;
  eventIcon: (k: FeedEvent['kind']) => string;
}

export function setup(): Setup {
  const feed = createFeed();
  feed.start();

  const filter = signal<string>('all');
  const accent = signal<string>('violet');
  const activeNav = signal<string>('Overview');
  const openPanels = signal<string[]>(['eu']);

  // Settings-view form state.
  const workspace = signal<string>('Acme Payments');
  const region = signal<string>('eu');
  const emailAlerts = signal<boolean>(true);
  const density = signal<string>('comfortable');

  const bars = computed(() => buildBars(feed.series()));
  const visibleTxns = computed<Txn[]>(() =>
    filter() === 'all' ? feed.txns() : feed.txns().filter((t) => t.status === filter()),
  );

  const columns = [
    { key: 'merchant', header: 'Merchant' },
    { key: 'method', header: 'Method' },
    { key: 'amount', header: 'Amount', numeric: true, cell: (t: Txn) => money(t.amount) },
    { key: 'status', header: 'Status', cell: (t: Txn) => statusPill(t.status) },
    { key: 'actions', header: '', cell: (t: Txn) => actionButton(t) },
  ];

  const setAccent = (key: string): void => {
    const a = ACCENTS.find((x) => x.key === key);
    if (!a) return;
    // Re-tune the *token* live — every component + custom chunk re-skins instantly.
    document.documentElement.style.setProperty('--weave-color-accent', a.color);
    accent.set(key);
  };

  return {
    nav: NAV,
    accents: ACCENTS,
    filterOpts: FILTER_OPTS,
    statusPanels: STATUS_PANELS,
    live: feed.live,
    now: feed.now,
    revenue: feed.revenue,
    users: feed.users,
    orders: feed.orders,
    latency: feed.latency,
    revenueDelta: feed.revenueDelta,
    usersDelta: feed.usersDelta,
    events: feed.events,
    bars,
    visibleTxns,
    columns,
    trackTxn: (t) => t.id,
    filter,
    accent,
    activeNav,
    openPanels,
    navSub: () => NAV_SUB[activeNav()] ?? '',
    regionOpts: REGION_OPTS,
    densityOpts: DENSITY_OPTS,
    workspace,
    region,
    emailAlerts,
    density,
    setLive: (v) => feed.live.set(v),
    setFilter: (v) => filter.set(v as string),
    setAccent,
    selectNav: (label) => activeNav.set(label),
    setOpenPanels: (v) => openPanels.set(v),
    setWorkspace: (v) => workspace.set(v),
    setRegion: (v) => region.set(v as string),
    setEmailAlerts: (v) => emailAlerts.set(v),
    setDensity: (v) => density.set(v as string),
    saveSettings: () =>
      snackbar(`Settings saved for “${workspace()}”`, { action: 'Dismiss', duration: 3000 }),
    money,
    moneyCompact,
    num,
    rel,
    eventIcon: (k) => EVENT_ICON[k],
  };
}

import { defineComponent } from "@weave-framework/runtime/dom";

import { bindAttr, bindText, child, clone, cloneFragment, eachBlock, ifBlock, listen, mountChild, setAttr, template, templateSvg } from "@weave-framework/runtime/dom";

const _t0 = template("<button data-w-17af97 type=\"button\">\n          <!---->\n          <span data-w-17af97><!----><\/span>\n        <\/button>");
const _t1 = template("<button data-w-17af97 type=\"button\"><\/button>");
const _t2 = template("<span data-w-17af97 class=\"kpi__delta kpi__delta--up\"><!---->+<!---->/s<\/span>");
const _t3 = template("<span data-w-17af97 class=\"kpi__delta kpi__delta--down\"><!---->−<!---->/s<\/span>");
const _t4 = template("<span data-w-17af97 class=\"kpi__label\">Revenue today<\/span><span data-w-17af97 class=\"kpi__value\"><!----><\/span><!---->");
const _t5 = template("<span data-w-17af97 class=\"kpi__delta kpi__delta--up\"><!---->+<!----><\/span>");
const _t6 = template("<span data-w-17af97 class=\"kpi__delta kpi__delta--down\"><!---->−<!----><\/span>");
const _t7 = template("<span data-w-17af97 class=\"kpi__label\">Active users<\/span><span data-w-17af97 class=\"kpi__value\"><!----><\/span><!---->");
const _t8 = template("<span data-w-17af97 class=\"kpi__label\">Orders<\/span><span data-w-17af97 class=\"kpi__value\"><!----><\/span><span data-w-17af97 class=\"kpi__delta kpi__delta--muted\">cumulative<\/span>");
const _t9 = template("<span data-w-17af97 class=\"kpi__delta kpi__delta--up\">healthy<\/span>");
const _t10 = template("<span data-w-17af97 class=\"kpi__delta kpi__delta--down\">elevated<\/span>");
const _t11 = template("<span data-w-17af97 class=\"kpi__label\">p95 latency<\/span><span data-w-17af97 class=\"kpi__value\"><!----><small data-w-17af97> ms<\/small><\/span><!---->");
const _t12 = template("<section data-w-17af97 class=\"kpis\">\n        <!---->\n        <!---->\n        <!---->\n        <!---->\n      <\/section>");
const _t13 = template("req/s");
const _t14 = templateSvg("<rect data-w-17af97 class=\"chart__bar\"><\/rect>");
const _t15 = template("<div data-w-17af97 class=\"panel__head\">\n            <h2 data-w-17af97 class=\"panel__title\">Throughput<\/h2>\n            <!---->\n          <\/div><svg data-w-17af97 class=\"chart\" viewBox=\"0 0 100 100\" preserveAspectRatio=\"none\" role=\"img\">\n            <defs data-w-17af97>\n              <linearGradient data-w-17af97 id=\"chartBar\" x1=\"0\" y1=\"1\" x2=\"0\" y2=\"0\">\n                <stop data-w-17af97 class=\"chart__from\" offset=\"0\"><\/stop>\n                <stop data-w-17af97 class=\"chart__to\" offset=\"1\"><\/stop>\n              <\/linearGradient>\n              <linearGradient data-w-17af97 id=\"chartBarLast\" x1=\"0\" y1=\"1\" x2=\"0\" y2=\"0\">\n                <stop data-w-17af97 class=\"chart__from chart__from--last\" offset=\"0\"><\/stop>\n                <stop data-w-17af97 class=\"chart__to chart__to--last\" offset=\"1\"><\/stop>\n              <\/linearGradient>\n            <\/defs>\n            <!---->\n          <\/svg>");
const _t16 = template("<span data-w-17af97 class=\"live\"><span data-w-17af97 class=\"live__dot\"><\/span>Live<\/span>");
const _t17 = template("<span data-w-17af97 class=\"live live--paused\">Paused<\/span>");
const _t18 = template("<li data-w-17af97>\n                <span data-w-17af97 class=\"feed__icon\"><!----><\/span>\n                <span data-w-17af97 class=\"feed__text\"><!----><\/span>\n                <span data-w-17af97 class=\"feed__ago\"><!----><\/span>\n              <\/li>");
const _t19 = template("<div data-w-17af97 class=\"panel__head\">\n            <h2 data-w-17af97 class=\"panel__title\">Activity<\/h2>\n            <!---->\n          <\/div><ul data-w-17af97 class=\"feed\">\n            <!---->\n          <\/ul>");
const _t20 = template("3 regions");
const _t21 = template("<div data-w-17af97 class=\"panel__head\">\n          <h2 data-w-17af97 class=\"panel__title\">System status<\/h2>\n          <!---->\n        <\/div><!---->");
const _t22 = template("<div data-w-17af97 class=\"panel__head\">\n          <h2 data-w-17af97 class=\"panel__title\">Recent transactions<\/h2>\n          <div data-w-17af97 class=\"panel__tools\">\n            <!---->\n          <\/div>\n        <\/div><!---->");
const _t23 = template("<section data-w-17af97 class=\"panels\">\n        <!---->\n        <!---->\n      <\/section><!----><!---->");
const _t24 = template("<div data-w-17af97 class=\"panel__head\">\n          <h2 data-w-17af97 class=\"panel__title\">All transactions<\/h2>\n          <div data-w-17af97 class=\"panel__tools\">\n            <!---->\n          <\/div>\n        <\/div><!---->");
const _t25 = template("<!---->");
const _t26 = template("<span data-w-17af97 class=\"live\"><span data-w-17af97 class=\"live__dot\"><\/span>Live<\/span>");
const _t27 = template("<span data-w-17af97 class=\"live live--paused\">Paused<\/span>");
const _t28 = template("<li data-w-17af97>\n                <span data-w-17af97 class=\"feed__icon\"><!----><\/span>\n                <span data-w-17af97 class=\"feed__text\"><!----><\/span>\n                <span data-w-17af97 class=\"feed__ago\"><!----><\/span>\n              <\/li>");
const _t29 = template("<div data-w-17af97 class=\"panel__head\">\n            <h2 data-w-17af97 class=\"panel__title\">Customer activity<\/h2>\n            <!---->\n          <\/div><ul data-w-17af97 class=\"feed\">\n            <!---->\n          <\/ul>");
const _t30 = template("/min");
const _t31 = templateSvg("<rect data-w-17af97 class=\"chart__bar\"><\/rect>");
const _t32 = template("<div data-w-17af97 class=\"panel__head\">\n            <h2 data-w-17af97 class=\"panel__title\">Sign-up rate<\/h2>\n            <!---->\n          <\/div><svg data-w-17af97 class=\"chart\" viewBox=\"0 0 100 100\" preserveAspectRatio=\"none\" role=\"img\">\n            <defs data-w-17af97>\n              <linearGradient data-w-17af97 id=\"chartBar\" x1=\"0\" y1=\"1\" x2=\"0\" y2=\"0\">\n                <stop data-w-17af97 class=\"chart__from\" offset=\"0\"><\/stop>\n                <stop data-w-17af97 class=\"chart__to\" offset=\"1\"><\/stop>\n              <\/linearGradient>\n              <linearGradient data-w-17af97 id=\"chartBarLast\" x1=\"0\" y1=\"1\" x2=\"0\" y2=\"0\">\n                <stop data-w-17af97 class=\"chart__from chart__from--last\" offset=\"0\"><\/stop>\n                <stop data-w-17af97 class=\"chart__to chart__to--last\" offset=\"1\"><\/stop>\n              <\/linearGradient>\n            <\/defs>\n            <!---->\n          <\/svg>");
const _t33 = template("<section data-w-17af97 class=\"panels\">\n        <!---->\n        <!---->\n      <\/section>");
const _t34 = template("3 regions");
const _t35 = template("<div data-w-17af97 class=\"panel__head\">\n          <h2 data-w-17af97 class=\"panel__title\">Region health<\/h2>\n          <!---->\n        <\/div><!---->");
const _t36 = template("ms");
const _t37 = templateSvg("<rect data-w-17af97 class=\"chart__bar\"><\/rect>");
const _t38 = template("<div data-w-17af97 class=\"panel__head\">\n          <h2 data-w-17af97 class=\"panel__title\">Latency (p95)<\/h2>\n          <!---->\n        <\/div><svg data-w-17af97 class=\"chart\" viewBox=\"0 0 100 100\" preserveAspectRatio=\"none\" role=\"img\">\n          <defs data-w-17af97>\n            <linearGradient data-w-17af97 id=\"chartBar\" x1=\"0\" y1=\"1\" x2=\"0\" y2=\"0\">\n              <stop data-w-17af97 class=\"chart__from\" offset=\"0\"><\/stop>\n              <stop data-w-17af97 class=\"chart__to\" offset=\"1\"><\/stop>\n            <\/linearGradient>\n            <linearGradient data-w-17af97 id=\"chartBarLast\" x1=\"0\" y1=\"1\" x2=\"0\" y2=\"0\">\n              <stop data-w-17af97 class=\"chart__from chart__from--last\" offset=\"0\"><\/stop>\n              <stop data-w-17af97 class=\"chart__to chart__to--last\" offset=\"1\"><\/stop>\n            <\/linearGradient>\n          <\/defs>\n          <!---->\n        <\/svg>");
const _t39 = template("<!----><!---->");
const _t40 = template("Save changes");
const _t41 = template("<div data-w-17af97 class=\"panel__head\">\n          <h2 data-w-17af97 class=\"panel__title\">Workspace settings<\/h2>\n        <\/div><div data-w-17af97 class=\"settings__grid\">\n          <!---->\n          <!---->\n          <!---->\n          <div data-w-17af97 class=\"settings__toggles\">\n            <!---->\n            <!---->\n          <\/div>\n        <\/div><div data-w-17af97 class=\"settings__actions\">\n          <!---->\n        <\/div>");
const _t42 = template("<!---->");
const _t43 = template("<div data-w-17af97 class=\"shell\">\n  <aside data-w-17af97 class=\"sidebar\">\n    <div data-w-17af97 class=\"brand\">\n      <span data-w-17af97 class=\"brand__mark\"><\/span>\n      <span data-w-17af97 class=\"brand__name\">Weave<b data-w-17af97>Analytics<\/b><\/span>\n    <\/div>\n\n    <nav data-w-17af97 class=\"nav\">\n      <!---->\n    <\/nav>\n\n    <div data-w-17af97 class=\"sidebar__foot\">\n      <span data-w-17af97 class=\"sidebar__dot\"><\/span>\n      Built with Weave — same components, a different skin. Try the swatches ↗\n    <\/div>\n  <\/aside>\n\n  <div data-w-17af97 class=\"main\">\n    <header data-w-17af97 class=\"topbar\">\n      <div data-w-17af97>\n        <h1 data-w-17af97 class=\"topbar__title\"><!----><\/h1>\n        <p data-w-17af97 class=\"topbar__sub\"><!----><\/p>\n      <\/div>\n      <div data-w-17af97 class=\"topbar__right\">\n        <div data-w-17af97 class=\"accent-switch\" role=\"group\" aria-label=\"Accent colour\">\n          <!---->\n        <\/div>\n        <!---->\n        <span data-w-17af97 class=\"latency\"><!----> <!----> ms<\/span>\n      <\/div>\n    <\/header>\n\n    <!---->\n\n    <!---->\n\n    <!---->\n\n    <!---->\n\n    <!---->\n\n    <!---->\n  <\/div>\n<\/div>");
function render(ctx, slots) {
  const _r = clone(_t43);
  const _n0 = child(_r, 1, 3, 1);
  const _n1 = child(_r, 3, 1, 1, 1, 0);
  const _n2 = child(_r, 3, 1, 1, 3, 0);
  const _n3 = child(_r, 3, 1, 3, 1, 1);
  const _n4 = child(_r, 3, 1, 3, 3);
  const _n5 = child(_r, 3, 1, 3, 5, 0);
  const _n6 = child(_r, 3, 1, 3, 5, 2);
  const _n7 = child(_r, 3, 3);
  const _n8 = child(_r, 3, 5);
  const _n9 = child(_r, 3, 7);
  const _n10 = child(_r, 3, 9);
  const _n11 = child(_r, 3, 11);
  const _n12 = child(_r, 3, 13);
  eachBlock(_n0, () => ctx.nav, (item, $index) => item.label, _b0)
  bindText(_n1, () => (ctx.activeNav()));
  bindText(_n2, () => (ctx.navSub()));
  eachBlock(_n3, () => ctx.accents, (a, $index) => a.key, _b1)
  mountChild(_n4, SlideToggle({ get checked() { return ctx.live(); }, get onChange() { return ctx.setLive; }, get label() { return 'Live feed'; } }, {}));
  mountChild(_n5, Icon({ get name() { return 'clock'; } }, {}));
  bindText(_n6, () => (ctx.latency()));
  ifBlock(_n7, () => { if (ctx.activeNav() !== 'Settings') return _b2; return null; })
  ifBlock(_n8, () => { if (ctx.activeNav() === 'Overview') return _b13; return null; })
  ifBlock(_n9, () => { if (ctx.activeNav() === 'Payments') return _b24; return null; })
  ifBlock(_n10, () => { if (ctx.activeNav() === 'Customers') return _b26; return null; })
  ifBlock(_n11, () => { if (ctx.activeNav() === 'Alerts') return _b34; return null; })
  ifBlock(_n12, () => { if (ctx.activeNav() === 'Settings') return _b40; return null; })
  return _r;
  function _b0(_row0) {
  const _r = clone(_t0);
  const _n0 = child(_r, 1);
  const _n1 = child(_r, 3, 0);
  bindAttr(_r, "class", () => (_row0.item().label === ctx.activeNav() ? 'nav__item nav__item--active' : 'nav__item'));
  listen(_r, "click", () => ctx.selectNav(_row0.item().label));
  mountChild(_n0, Icon({ get name() { return _row0.item().icon; } }, {}));
  bindText(_n1, () => (_row0.item().label));
  return _r;
}
  function _b1(_row1) {
  const _r = clone(_t1);
  bindAttr(_r, "class", () => (_row1.item().key === ctx.accent() ? 'swatch swatch--on' : 'swatch'));
  bindAttr(_r, "style", () => ('background:' + _row1.item().color));
  bindAttr(_r, "aria-label", () => (_row1.item().key));
  listen(_r, "click", () => ctx.setAccent(_row1.item().key));
  return _r;
}
  function _b2() {
  const _r = clone(_t12);
  const _n0 = child(_r, 1);
  const _n1 = child(_r, 3);
  const _n2 = child(_r, 5);
  const _n3 = child(_r, 7);
  mountChild(_n0, Card({ class: "kpi" }, { default: _s3 }));
  mountChild(_n1, Card({ class: "kpi" }, { default: _s6 }));
  mountChild(_n2, Card({ class: "kpi" }, { default: _s9 }));
  mountChild(_n3, Card({ class: "kpi" }, { default: _s10 }));
  return _r;
  function _s3() {
  const _r = cloneFragment(_t4);
  const _n0 = child(_r, 1, 0);
  const _n1 = child(_r, 2);
  bindText(_n0, () => (ctx.moneyCompact(ctx.revenue())));
  ifBlock(_n1, () => { if (ctx.revenueDelta() >= 0) return _b4; return _b5; })
  return _r;
  function _b4() {
  const _r = clone(_t2);
  const _n0 = child(_r, 0);
  const _n1 = child(_r, 2);
  mountChild(_n0, Icon({ get name() { return 'arrow-up'; } }, {}));
  bindText(_n1, () => (ctx.money(ctx.revenueDelta())));
  return _r;
}
  function _b5() {
  const _r = clone(_t3);
  const _n0 = child(_r, 0);
  const _n1 = child(_r, 2);
  mountChild(_n0, Icon({ get name() { return 'arrow-down'; } }, {}));
  bindText(_n1, () => (ctx.money(-ctx.revenueDelta())));
  return _r;
}
}
  function _s6() {
  const _r = cloneFragment(_t7);
  const _n0 = child(_r, 1, 0);
  const _n1 = child(_r, 2);
  bindText(_n0, () => (ctx.num(ctx.users())));
  ifBlock(_n1, () => { if (ctx.usersDelta() >= 0) return _b7; return _b8; })
  return _r;
  function _b7() {
  const _r = clone(_t5);
  const _n0 = child(_r, 0);
  const _n1 = child(_r, 2);
  mountChild(_n0, Icon({ get name() { return 'arrow-up'; } }, {}));
  bindText(_n1, () => (ctx.num(ctx.usersDelta())));
  return _r;
}
  function _b8() {
  const _r = clone(_t6);
  const _n0 = child(_r, 0);
  const _n1 = child(_r, 2);
  mountChild(_n0, Icon({ get name() { return 'arrow-down'; } }, {}));
  bindText(_n1, () => (ctx.num(-ctx.usersDelta())));
  return _r;
}
}
  function _s9() {
  const _r = cloneFragment(_t8);
  const _n0 = child(_r, 1, 0);
  bindText(_n0, () => (ctx.num(ctx.orders())));
  return _r;
}
  function _s10() {
  const _r = cloneFragment(_t11);
  const _n0 = child(_r, 1, 0);
  const _n1 = child(_r, 2);
  bindText(_n0, () => (ctx.latency()));
  ifBlock(_n1, () => { if (ctx.latency() < 90) return _b11; return _b12; })
  return _r;
  function _b11() {
  const _r = clone(_t9);
  return _r;
}
  function _b12() {
  const _r = clone(_t10);
  return _r;
}
}
}
  function _b13() {
  const _r = cloneFragment(_t23);
  const _n0 = child(_r, 0, 1);
  const _n1 = child(_r, 0, 3);
  const _n2 = child(_r, 1);
  const _n3 = child(_r, 2);
  mountChild(_n0, Card({ class: "panel panel--chart" }, { default: _s14 }));
  mountChild(_n1, Card({ class: "panel panel--feed" }, { default: _s17 }));
  mountChild(_n2, Card({ class: "panel" }, { default: _s21 }));
  mountChild(_n3, Card({ class: "panel panel--table" }, { default: _s23 }));
  return _r;
  function _s14() {
  const _r = cloneFragment(_t15);
  const _n0 = child(_r, 0, 3);
  const _n1 = child(_r, 1);
  const _n2 = child(_r, 1, 3);
  mountChild(_n0, Badge({ get variant() { return 'tag'; } }, { default: _s15 }));
  setAttr(_n1, "aria-label", 'Bar chart sparkline');
  eachBlock(_n2, () => ctx.bars(), (b, $index) => b.i, _b16)
  return _r;
  function _s15() {
  const _r = cloneFragment(_t13);
  return _r;
}
  function _b16(_row2) {
  const _r = clone(_t14);
  bindAttr(_r, "x", () => (_row2.item().x));
  bindAttr(_r, "y", () => (_row2.item().y));
  bindAttr(_r, "width", () => (_row2.item().w));
  bindAttr(_r, "height", () => (_row2.item().h));
  bindAttr(_r, "fill", () => (_row2.item().last ? 'url(#chartBarLast)' : 'url(#chartBar)'));
  return _r;
}
}
  function _s17() {
  const _r = cloneFragment(_t19);
  const _n0 = child(_r, 0, 3);
  const _n1 = child(_r, 1, 1);
  ifBlock(_n0, () => { if (ctx.live()) return _b18; return _b19; })
  eachBlock(_n1, () => ctx.events(), (e, $index) => e.id, _b20)
  return _r;
  function _b18() {
  const _r = clone(_t16);
  return _r;
}
  function _b19() {
  const _r = clone(_t17);
  return _r;
}
  function _b20(_row3) {
  const _r = clone(_t18);
  const _n0 = child(_r, 1, 0);
  const _n1 = child(_r, 3, 0);
  const _n2 = child(_r, 5, 0);
  bindAttr(_r, "class", () => ('feed__row feed__row--' + _row3.item().kind));
  mountChild(_n0, Icon({ get name() { return ctx.eventIcon(_row3.item().kind); } }, {}));
  bindText(_n1, () => (_row3.item().text));
  bindText(_n2, () => (ctx.rel(ctx.now() - _row3.item().createdAt)));
  return _r;
}
}
  function _s21() {
  const _r = cloneFragment(_t21);
  const _n0 = child(_r, 0, 3);
  const _n1 = child(_r, 1);
  mountChild(_n0, Badge({ get variant() { return 'tag'; } }, { default: _s22 }));
  mountChild(_n1, Expansion({ get panels() { return ctx.statusPanels; }, get value() { return ctx.openPanels(); }, get onChange() { return ctx.setOpenPanels; } }, {}));
  return _r;
  function _s22() {
  const _r = cloneFragment(_t20);
  return _r;
}
}
  function _s23() {
  const _r = cloneFragment(_t22);
  const _n0 = child(_r, 0, 3, 1);
  const _n1 = child(_r, 1);
  mountChild(_n0, Select({ get options() { return ctx.filterOpts; }, get value() { return ctx.filter(); }, get onChange() { return ctx.setFilter; }, get label() { return 'Filter'; } }, {}));
  mountChild(_n1, Table({ get columns() { return ctx.columns; }, get dataSource() { return ctx.visibleTxns; }, get trackBy() { return ctx.trackTxn; }, get ariaLabel() { return 'Recent transactions'; } }, {}));
  return _r;
}
}
  function _b24() {
  const _r = cloneFragment(_t25);
  const _n0 = child(_r, 0);
  mountChild(_n0, Card({ class: "panel panel--table" }, { default: _s25 }));
  return _r;
  function _s25() {
  const _r = cloneFragment(_t24);
  const _n0 = child(_r, 0, 3, 1);
  const _n1 = child(_r, 1);
  mountChild(_n0, Select({ get options() { return ctx.filterOpts; }, get value() { return ctx.filter(); }, get onChange() { return ctx.setFilter; }, get label() { return 'Filter'; } }, {}));
  mountChild(_n1, Table({ get columns() { return ctx.columns; }, get dataSource() { return ctx.visibleTxns; }, get trackBy() { return ctx.trackTxn; }, get ariaLabel() { return 'All transactions'; } }, {}));
  return _r;
}
}
  function _b26() {
  const _r = clone(_t33);
  const _n0 = child(_r, 1);
  const _n1 = child(_r, 3);
  mountChild(_n0, Card({ class: "panel panel--feed panel--wide" }, { default: _s27 }));
  mountChild(_n1, Card({ class: "panel" }, { default: _s31 }));
  return _r;
  function _s27() {
  const _r = cloneFragment(_t29);
  const _n0 = child(_r, 0, 3);
  const _n1 = child(_r, 1, 1);
  ifBlock(_n0, () => { if (ctx.live()) return _b28; return _b29; })
  eachBlock(_n1, () => ctx.events(), (e, $index) => e.id, _b30)
  return _r;
  function _b28() {
  const _r = clone(_t26);
  return _r;
}
  function _b29() {
  const _r = clone(_t27);
  return _r;
}
  function _b30(_row4) {
  const _r = clone(_t28);
  const _n0 = child(_r, 1, 0);
  const _n1 = child(_r, 3, 0);
  const _n2 = child(_r, 5, 0);
  bindAttr(_r, "class", () => ('feed__row feed__row--' + _row4.item().kind));
  mountChild(_n0, Icon({ get name() { return ctx.eventIcon(_row4.item().kind); } }, {}));
  bindText(_n1, () => (_row4.item().text));
  bindText(_n2, () => (ctx.rel(ctx.now() - _row4.item().createdAt)));
  return _r;
}
}
  function _s31() {
  const _r = cloneFragment(_t32);
  const _n0 = child(_r, 0, 3);
  const _n1 = child(_r, 1);
  const _n2 = child(_r, 1, 3);
  mountChild(_n0, Badge({ get variant() { return 'tag'; } }, { default: _s32 }));
  setAttr(_n1, "aria-label", 'Bar chart sparkline');
  eachBlock(_n2, () => ctx.bars(), (b, $index) => b.i, _b33)
  return _r;
  function _s32() {
  const _r = cloneFragment(_t30);
  return _r;
}
  function _b33(_row5) {
  const _r = clone(_t31);
  bindAttr(_r, "x", () => (_row5.item().x));
  bindAttr(_r, "y", () => (_row5.item().y));
  bindAttr(_r, "width", () => (_row5.item().w));
  bindAttr(_r, "height", () => (_row5.item().h));
  bindAttr(_r, "fill", () => (_row5.item().last ? 'url(#chartBarLast)' : 'url(#chartBar)'));
  return _r;
}
}
}
  function _b34() {
  const _r = cloneFragment(_t39);
  const _n0 = child(_r, 0);
  const _n1 = child(_r, 1);
  mountChild(_n0, Card({ class: "panel" }, { default: _s35 }));
  mountChild(_n1, Card({ class: "panel" }, { default: _s37 }));
  return _r;
  function _s35() {
  const _r = cloneFragment(_t35);
  const _n0 = child(_r, 0, 3);
  const _n1 = child(_r, 1);
  mountChild(_n0, Badge({ get variant() { return 'tag'; } }, { default: _s36 }));
  mountChild(_n1, Expansion({ get panels() { return ctx.statusPanels; }, get value() { return ctx.openPanels(); }, get onChange() { return ctx.setOpenPanels; } }, {}));
  return _r;
  function _s36() {
  const _r = cloneFragment(_t34);
  return _r;
}
}
  function _s37() {
  const _r = cloneFragment(_t38);
  const _n0 = child(_r, 0, 3);
  const _n1 = child(_r, 1);
  const _n2 = child(_r, 1, 3);
  mountChild(_n0, Badge({ get variant() { return 'tag'; } }, { default: _s38 }));
  setAttr(_n1, "aria-label", 'Bar chart sparkline');
  eachBlock(_n2, () => ctx.bars(), (b, $index) => b.i, _b39)
  return _r;
  function _s38() {
  const _r = cloneFragment(_t36);
  return _r;
}
  function _b39(_row6) {
  const _r = clone(_t37);
  bindAttr(_r, "x", () => (_row6.item().x));
  bindAttr(_r, "y", () => (_row6.item().y));
  bindAttr(_r, "width", () => (_row6.item().w));
  bindAttr(_r, "height", () => (_row6.item().h));
  bindAttr(_r, "fill", () => (_row6.item().last ? 'url(#chartBarLast)' : 'url(#chartBar)'));
  return _r;
}
}
}
  function _b40() {
  const _r = cloneFragment(_t42);
  const _n0 = child(_r, 0);
  mountChild(_n0, Card({ class: "panel settings" }, { default: _s41 }));
  return _r;
  function _s41() {
  const _r = cloneFragment(_t41);
  const _n0 = child(_r, 1, 1);
  const _n1 = child(_r, 1, 3);
  const _n2 = child(_r, 1, 5);
  const _n3 = child(_r, 1, 7, 1);
  const _n4 = child(_r, 1, 7, 3);
  const _n5 = child(_r, 2, 1);
  mountChild(_n0, Input({ get value() { return ctx.workspace(); }, get onInput() { return ctx.setWorkspace; }, get label() { return 'Workspace name'; } }, {}));
  mountChild(_n1, Select({ get options() { return ctx.regionOpts; }, get value() { return ctx.region(); }, get onChange() { return ctx.setRegion; }, get label() { return 'Default region'; } }, {}));
  mountChild(_n2, ButtonToggle({ get options() { return ctx.densityOpts; }, get value() { return ctx.density(); }, get onChange() { return ctx.setDensity; }, get label() { return 'Table density'; } }, {}));
  mountChild(_n3, SlideToggle({ get checked() { return ctx.live(); }, get onChange() { return ctx.setLive; }, get label() { return 'Live updates'; } }, {}));
  mountChild(_n4, Checkbox({ get checked() { return ctx.emailAlerts(); }, get onChange() { return ctx.setEmailAlerts; }, get label() { return 'Email me on alerts'; } }, {}));
  mountChild(_n5, Button({ onClick: ctx.saveSettings, '$events': ["onClick"] }, { default: _s42 }));
  return _r;
  function _s42() {
  const _r = cloneFragment(_t40);
  return _r;
}
}
}
}

export default defineComponent(render, setup);